import { useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Input, InputNumber, Modal, Select, Table, Tabs, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Plus } from 'lucide-react';
import Layout from '../../Layout';
import { priceRows } from './ktBooks';
import { ktApi, today, useKt } from './ktStore';
import { dmy, fmtMoney, rateLabel, rateOn, SERVICE_TYPE, splitVat, type TaxRate } from './ktLogic';
import { DemoBar, PageHeader, Panel, showErrors } from './ktUi';

const CUTOVER_BEFORE = '2026-12-31';
const CUTOVER_AFTER = '2027-01-01';

/** Thuế suất gắn theo từng mã và ngày hiệu lực; bảng giá hiện cả giá có và chưa VAT (SRS 1.4) */
export default function KeToanTaxRates() {
  const s = useKt();
  const now = today();
  const [adding, setAdding] = useState(false);
  const [serviceId, setServiceId] = useState(s.services[0]?.id);
  const [gross, setGross] = useState(11_000_000);
  const options = useMemo(() => s.services.map(x => ({ value: x.id, label: `${x.code} · ${x.name}` })), [s.services]);
  const svName = (id: string) => s.services.find(x => x.id === id);
  const rows = useMemo(() => [...s.rates].sort((a, b) => a.serviceId.localeCompare(b.serviceId) || b.from.localeCompare(a.from)), [s.rates]);
  const prices = useMemo(() => priceRows(s, now, CUTOVER_AFTER), [s, now]);
  const noRate = s.services.filter(x => !rateOn(s.rates, x.id, now));

  const columns: ColumnsType<TaxRate> = [
    { title: 'Mã', key: 'c', width: 170, render: (_, r) => svName(r.serviceId)?.code },
    { title: 'Tên', key: 'n', render: (_, r) => svName(r.serviceId)?.name },
    { title: 'Thuế suất', key: 'r', width: 150, render: (_, r) => <b>{rateLabel(r.rate)}</b> },
    { title: 'Hiệu lực từ', key: 'f', width: 120, render: (_, r) => dmy(r.from) },
    { title: 'Trạng thái', key: 's', width: 130, render: (_, r) => (
      rateOn(s.rates, r.serviceId, now)?.id === r.id ? <Tag color="green">Đang áp dụng</Tag> : r.from > now ? <Tag color="blue">Sắp áp dụng</Tag> : <Tag>Hết hiệu lực</Tag>
    ) },
    { title: 'Ghi chú', dataIndex: 'note', width: 260 },
  ];

  const priceCols: ColumnsType<typeof prices[number]> = [
    { title: 'Mã', key: 'c', width: 160, render: (_, r) => r.sv.code },
    { title: 'Tên', key: 'n', render: (_, r) => <div><div>{r.sv.name}</div><div className="text-[11px] text-gray-500">{SERVICE_TYPE[r.sv.type]} · {r.sv.group}</div></div> },
    { title: 'Giá có VAT', key: 'g', align: 'right', width: 130, render: (_, r) => fmtMoney(r.sv.price) },
    { title: 'Thuế suất nay', key: 'r', align: 'right', width: 120, render: (_, r) => rateLabel(r.now) },
    { title: 'Giá chưa VAT', key: 'net', align: 'right', width: 130, render: (_, r) => <b>{fmtMoney(r.net)}</b> },
    { title: 'Từ 01/01/2027', key: 'x', align: 'right', width: 120, render: (_, r) => rateLabel(r.next) },
    { title: 'Giữ giá có VAT: chưa VAT còn', key: 'kg', align: 'right', width: 150, render: (_, r) => fmtMoney(r.keepGrossNet) },
    { title: 'Giữ giá chưa VAT: có VAT thành', key: 'kn', align: 'right', width: 150, render: (_, r) => fmtMoney(r.keepNetGross) },
  ];

  const compare = [CUTOVER_BEFORE, CUTOVER_AFTER].map(date => {
    const r = rateOn(s.rates, serviceId, date);
    return { date, rate: r?.rate ?? null, ...splitVat(gross, r?.rate ?? null) };
  });

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Thuế suất và bảng giá" desc="Mỗi mã dịch vụ, sản phẩm có thuế suất theo ngày hiệu lực; bill tự lấy thuế suất đúng theo ngày bán"
          extra={s.tier === 'chief' && <Button type="primary" icon={<Plus size={14} />} onClick={() => setAdding(true)}>Thêm thuế suất</Button>} />
        <DemoBar />
        <Alert type="info" showIcon message="Mức 8%, 10% theo SRS và mức không chịu thuế của dịch vụ y tế là giả định để demo. Kế toán trưởng cần xác nhận từng mã, gồm cả việc dịch vụ thẩm mỹ có thuộc diện giảm thuế theo Nghị định 174/2025 không." />
        {noRate.length > 0 && <Alert type="warning" showIcon message={`${noRate.length} mã chưa có thuế suất, hóa đơn của các mã này sẽ bị chặn: ${noRate.map(x => x.code).join(', ')}`} />}

        <Panel title="Thử chuyển thuế suất qua mốc 01/01/2027">
          <div className="px-4 py-3 flex flex-col gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="kt-tax-service" className="block text-xs text-gray-500 mb-1">Mã</label>
                <Select id="kt-tax-service" className="w-72" showSearch optionFilterProp="label" value={serviceId} onChange={setServiceId} options={options} />
              </div>
              <div>
                <label htmlFor="kt-tax-gross" className="block text-xs text-gray-500 mb-1">Giá bán (gồm VAT)</label>
                <InputNumber id="kt-tax-gross" className="!w-44" min={0} step={100000} value={gross} onChange={v => setGross(Number(v) || 0)}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {compare.map(c => (
                <div key={c.date} className="rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-3">
                  <div className="text-xs text-gray-500">Bán ngày {dmy(c.date)}: thuế suất {rateLabel(c.rate)}</div>
                  <div className="text-sm mt-1">Chưa VAT <b>{fmtMoney(c.net)}</b></div>
                  <div className="text-sm">VAT <b>{fmtMoney(c.vat)}</b></div>
                </div>
              ))}
            </div>
          </div>
        </Panel>

        <Tabs items={[
          { key: 'price', label: `Bảng giá (${prices.length})`, children: (
            <Panel title="Bảng giá có VAT và chưa VAT" extra={<span className="text-xs text-gray-500">Hai cột cuối là 2 cách xử lý khi đổi thuế suất, Kế toán trưởng chọn một</span>}>
              <Table rowKey={r => r.sv.id} size="middle" columns={priceCols} dataSource={prices} pagination={{ pageSize: 12, hideOnSinglePage: true, showSizeChanger: false }} scroll={{ x: 1150 }} />
            </Panel>
          ) },
          { key: 'rates', label: `Thuế suất theo mã (${rows.length})`, children: (
            <Panel title="Bảng thuế suất">
              <Table rowKey="id" size="small" columns={columns} dataSource={rows} pagination={{ pageSize: 14, showSizeChanger: false }} scroll={{ x: 1000 }} />
            </Panel>
          ) },
        ]} />
      </div>
      {adding && <AddRateModal options={options} onClose={() => setAdding(false)} />}
    </Layout>
  );
}

function AddRateModal({ options, onClose }: { options: { value: string; label: string }[]; onClose: () => void }) {
  const [ids, setIds] = useState<string[]>([]);
  const [kind, setKind] = useState<'taxed' | 'exempt'>('taxed');
  const [rate, setRate] = useState(10);
  const [from, setFrom] = useState<dayjs.Dayjs | null>(null);
  const [note, setNote] = useState('');
  return (
    <Modal open title="Thêm thuế suất" okText="Lưu" cancelText="Thôi" onCancel={onClose} destroyOnHidden
      onOk={() => {
        const r = ktApi.addRate(ids, kind === 'exempt' ? null : rate, from ? from.format('YYYY-MM-DD') : '', note.trim());
        if (r.ok) { message.success('Đã thêm thuế suất'); onClose(); } else showErrors(r.errors);
      }}>
      <div className="space-y-3">
        <div>
          <label htmlFor="kt-add-ids" className="text-sm font-medium">Mã áp dụng (chọn được nhiều mã)</label>
          <Select id="kt-add-ids" mode="multiple" className="w-full" optionFilterProp="label" value={ids} onChange={setIds} options={options} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="kt-add-kind" className="text-sm font-medium">Loại</label>
            <Select id="kt-add-kind" className="w-full" value={kind} onChange={setKind} options={[{ value: 'taxed', label: 'Chịu thuế' }, { value: 'exempt', label: 'Không chịu thuế' }]} />
          </div>
          <div>
            <label htmlFor="kt-add-rate" className="text-sm font-medium">Thuế suất (%)</label>
            <InputNumber id="kt-add-rate" className="!w-full" min={0} max={100} disabled={kind === 'exempt'} value={rate} onChange={v => setRate(Number(v) || 0)} />
          </div>
        </div>
        <div><label htmlFor="kt-add-from" className="text-sm font-medium">Hiệu lực từ</label><DatePicker id="kt-add-from" className="w-full" format="DD/MM/YYYY" value={from} onChange={setFrom} /></div>
        <div><label htmlFor="kt-add-note" className="text-sm font-medium">Ghi chú</label><Input id="kt-add-note" value={note} onChange={e => setNote(e.target.value)} /></div>
      </div>
    </Modal>
  );
}
